import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import cls from './SelectIntegration.module.css';
import { Option } from './Option/Option';
import { Loader } from '../Loader/Loader';

export const SelectIntegration = ({
    selected,
    options,
    placeholder,
    status,
    onChange,
    onClose,
    title,
    isRequired,
    isDisabled = false,
    error = null,
    isLoading = false,
    isSmartPosition = false,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [optionFilter, setOptionFilter] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const rootRef = useRef(null);

    useEffect(() => {
        const handleClick = (event) => {
            const { target } = event;
            if (target instanceof Node && !rootRef.current?.contains(target)) {
                isOpen && onClose?.();
                setIsOpen(false);
                setOptionFilter('');
            }
        };
        window.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        const calcRect = () => {
            if (!isSmartPosition) {
                setDropdownStyle({});
                return;
            }

            if (!rootRef.current) return;

            const rect = rootRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < 170;

            setDropdownStyle({
                position: 'fixed',
                top: openUpward ? rect.top - 8 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                transform: openUpward ? 'translateY(-100%)' : 'none',
            });
        };

        calcRect();

        window.addEventListener('resize', calcRect);
        window.addEventListener('scroll', calcRect, true);

        return () => {
            window.removeEventListener('resize', calcRect);
            window.removeEventListener('scroll', calcRect, true);
        };
    }, [isOpen, isSmartPosition]);

    const handleOptionClick = (value) => {
        setIsOpen(false);
        onChange(value);
    };
    const handlePlaceHolderClick = () => {
        if (!isDisabled) {
            if (isOpen) {
                setOptionFilter('');
            }
            setIsOpen(!isOpen);
        }
    };

    const handleOptionFilter = (event) => {
        setOptionFilter(event.target.value);
    };
    return (
        <div className={classNames(cls.formBlockContent)}>
            {title && (
                <label className={classNames(cls.modalLabelSelect, { [cls.disabled]: isDisabled })}>
                    {title}
                    {isRequired && <span>*</span>}
                </label>
            )}
            <div
                onClick={handlePlaceHolderClick}
                className={classNames(cls.selectWrapper, { [cls.disabled]: isDisabled })}
                ref={rootRef}
                data-is-active={isOpen}
            >
                <div className={classNames(cls.arrow)}>
                    <svg
                        width="9"
                        height="6"
                        viewBox="0 0 9 6"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M4.5 5.5L9 0.5H0L4.5 5.5Z" fill="currentColor" fillOpacity="0.6" />
                    </svg>
                </div>
                {selected?.shortNumber ? (
                    <div className={classNames(cls.selected, { [cls.disabled]: isDisabled })}>
                        {selected.shortNumber === t('SelectIntegration.noNumber.text')
                            ? ''
                            : `${t('SelectIntegration.Account.text')} `}
                        {selected.shortNumber}
                    </div>
                ) : (
                    <div className={classNames(cls.placeholder, { [cls.disabled]: isDisabled })}>
                        {placeholder}
                    </div>
                )}
                {isOpen && (
                    <ul className={classNames(cls.option)} style={dropdownStyle}>
                        <div
                            className={classNames(cls.filterInput)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                className={classNames(cls.input)}
                                onChange={(event) => handleOptionFilter(event)}
                            />
                        </div>
                        {isLoading && (
                            <div className={classNames(cls.loader)}>
                                <Loader />
                            </div>
                        )}

                        {!isLoading &&
                            !!options.length &&
                            options
                                .filter(({ shortNumber, _ }) =>
                                    shortNumber.toLowerCase().includes(optionFilter.toLowerCase())
                                )
                                .map((option) => (
                                    <Option
                                        key={option.value}
                                        option={option}
                                        onClick={handleOptionClick}
                                        setIsOpen={setIsOpen}
                                    />
                                ))}
                        {!isLoading && !options.length && (
                            <div className={classNames(cls.emptyList)}>
                                {t('SelectIntegration.NoBankIntegration.text')}
                            </div>
                        )}
                    </ul>
                )}
            </div>
            {error && <span className={classNames(cls.errorMessage)}>{error}</span>}
        </div>
    );
};
